import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

// Compute age from a DOB string (YYYY-MM-DD). Falls back to stored age if no DOB.
function calcAge(dob, storedAge) {
  if (!dob) return storedAge || '—'
  const today = new Date()
  const birth  = new Date(dob)
  if (isNaN(birth.getTime())) return storedAge || '—'
  let age = today.getFullYear() - birth.getFullYear()
  const beforeBirthday =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  if (beforeBirthday) age--
  return age
}
const TEAL = '#0d9488'
const PURPLE = '#7c3aed'
const PURPLE_LIGHT = '#f5f3ff'
const PURPLE_MID   = '#c4b5fd'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

function PatientList({ onSelectPatient, isDemo = false, onDeletePatient, clinicId, onViewAsPatient }) {
  const [patients, setPatients] = useState([])
  const [unreadCounts, setUnreadCounts] = useState({})
  const [search, setSearch] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [tab, setTab] = useState('active')

  useEffect(() => {
    fetchPatients()
    fetchUnread()
    const interval = setInterval(fetchUnread, 15000)
    return () => clearInterval(interval)
  }, [isDemo, clinicId])

  const fetchPatients = () => {
    const url = isDemo
      ? API + '/patients?demo=true'
      : API + '/patients?clinic_id=' + clinicId + '&demo=false'
    axios.get(url)
      .then(res => setPatients(res.data))
      .catch(err => console.error(err))
  }

  const fetchUnread = () => {
    const url = isDemo
      ? API + '/summary?demo=true'
      : API + '/summary?clinic_id=' + clinicId + '&demo=false'
    axios.get(url).then(res => {
      const counts = {}
      res.data.patients.forEach(p => { counts[p.id] = p.unread_messages || 0 })
      setUnreadCounts(counts)
    }).catch(() => {})
  }

  const confirmDeletePatient = () => {
    if (!confirmDelete) return
    axios.delete(API + '/patients/' + confirmDelete.id)
      .then(() => {
        setPatients(p => p.filter(x => x.id !== confirmDelete.id))
        if (onDeletePatient) onDeletePatient(confirmDelete.id, confirmDelete.name)
        setConfirmDelete(null)
      })
      .catch(() => alert('Failed to delete patient.'))
  }

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0)
  const filtered = patients.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.injury_type && p.injury_type.toLowerCase().includes(search.toLowerCase())) ||
    (p.patient_email && p.patient_email.toLowerCase().includes(search.toLowerCase()))
  )
  const activePatients = filtered.filter(p => !p.is_discharged)
  const dischargedPatients = filtered.filter(p => p.is_discharged)
  const displayPatients = tab === 'active' ? activePatients : dischargedPatients

  const formatDischargeDate = (dt) => {
    if (!dt) return '—'
    try { return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
    catch { return '—' }
  }

  return (
    <div style={{ fontFamily: FONT }}>
      <style>{`
        .pl-search { width: 280px !important; }
        /* Large monitor — wider padding, bigger search bar */
        @media (min-width: 1440px) {
          .pl-search { width: 360px !important; }
          .pl-header-row, .pl-table-row { padding-left: 28px !important; padding-right: 28px !important; }
          .pl-table-row { padding-top: 16px !important; padding-bottom: 16px !important; }
          .pl-cell-id { font-size: 13px !important; }
        }
        /* Mobile */
        @media (max-width: 640px) {
          .pl-search { width: 100% !important; }
          .pl-header-row { display: none !important; }
          .pl-table-row {
            display: flex !important;
            flex-direction: column !important;
            padding: 14px 16px !important;
          }
          .pl-cell-id { display: none !important; }
          .pl-cell-injury { font-size: 12px !important; color: #64748b !important; }
          .pl-cell-email { display: none !important; }
          .pl-cell-age { display: none !important; }
          .pl-cell-intake { margin-top: 4px !important; }
          .pl-cell-actions { margin-top: 10px !important; justify-content: flex-start !important; }
        }
        @media (max-width: 960px) and (min-width: 641px) {
          .pl-cell-email { display: none !important; }
          .pl-cell-age { display: none !important; }
        }
      `}</style>

      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: 'white', borderRadius: '14px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}></div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: '0 0 8px' }}>Delete {confirmDelete.name}?</h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: '1.6' }}>
              This will permanently delete the patient and all their sessions, goals, exercises, and messages. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: FONT, color: '#334155' }}>Cancel</button>
              <button onClick={confirmDeletePatient} style={{ flex: 1, padding: '11px', border: 'none', borderRadius: '8px', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: FONT }}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
            Patients
            {totalUnread > 0 && (
              <span style={{ marginLeft: '10px', background: '#ef4444', color: 'white', borderRadius: '99px', fontSize: '11px', fontWeight: '700', padding: '2px 8px', verticalAlign: 'middle' }}>{totalUnread} new</span>
            )}
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '3px', marginBottom: 0 }}>
            {tab === 'active' ? activePatients.length : dischargedPatients.length} {tab} patient{(tab === 'active' ? activePatients.length : dischargedPatients.length) !== 1 ? 's' : ''}
          </p>
        </div>
        <input
          placeholder="Search by name, injury, or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-search"
          style={{ padding: '8px 14px', fontSize: '14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', width: '280px', color: '#1e293b', fontFamily: FONT, outline: 'none' }}
        />
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: '#f1f5f9', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
        {[['active', `Active (${patients.filter(p => !p.is_discharged).length})`], ['discharged', `Discharged (${patients.filter(p => p.is_discharged).length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '6px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: FONT, transition: 'all 0.15s', background: tab === key ? 'white' : 'transparent', color: tab === key ? (key === 'discharged' ? '#64748b' : PURPLE) : '#94a3b8', boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
            {label}
          </button>
        ))}
      </div>

      {patients.length === 0 && (
        <div style={{ background: 'rgba(255,255,255,0.78)', borderRadius: '10px', padding: '48px', textAlign: 'center', color: '#94a3b8', border: '1px solid #e2e8f0', fontSize: '15px' }}>
          No patients yet. Click "Add Patient" to get started.
        </div>
      )}

      {patients.length > 0 && (
        <div style={{ background: 'rgba(255,255,255,0.78)', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="pl-header-row" style={{ display: 'grid', gridTemplateColumns: '50px 2fr 1.5fr 1.5fr 80px 80px ' + (isDemo ? '140px' : '100px'), padding: '10px 20px', background: 'rgba(248,250,252,0.84)', borderBottom: '1px solid #e2e8f0' }}>
            {['ID', 'Patient', 'Injury', 'Email', 'Age', tab === 'discharged' ? 'Discharged' : 'Intake', ''].map((h, i) => (
              <div key={i} style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
            ))}
          </div>

          {displayPatients.map((p, i) => {
            const isDischarged = !!p.is_discharged
            return (
              <div key={p.id}
                className="pl-table-row"
                onClick={() => onSelectPatient(p)}
                style={{ display: 'grid', gridTemplateColumns: '50px 2fr 1.5fr 1.5fr 80px 80px ' + (isDemo ? '140px' : '100px'), padding: '13px 20px', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s', background: 'rgba(255,255,255,0.78)', alignItems: 'center', opacity: isDischarged ? 0.75 : 1 }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,250,252,0.90)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.78)'}
              >
                <div className="pl-cell-id" style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>{p.clinic_patient_id != null ? `#${p.clinic_patient_id}` : `#${p.id}`}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: isDischarged ? '#f8fafc' : (unreadCounts[p.id] > 0 ? '#fef2f2' : PURPLE_LIGHT), border: '1.5px solid ' + (isDischarged ? '#e2e8f0' : (unreadCounts[p.id] > 0 ? '#fecaca' : PURPLE_MID)), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: isDischarged ? '#94a3b8' : (unreadCounts[p.id] > 0 ? '#dc2626' : PURPLE), flexShrink: 0 }}>
                    {p.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: isDischarged ? '#64748b' : '#0f172a' }}>{p.name}</div>
                    {!isDischarged && unreadCounts[p.id] > 0 && <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: '500' }}>{unreadCounts[p.id]} unread</div>}
                    {isDischarged && <div style={{ fontSize: '11px', color: '#94a3b8' }}>Discharged</div>}
                  </div>
                </div>
                <div className="pl-cell-injury" style={{ fontSize: '13px', color: '#475569' }}>{p.injury_type || '—'}</div>
                <div className="pl-cell-email" style={{ fontSize: '12px', color: '#94a3b8' }}>{p.patient_email || '—'}</div>
                <div className="pl-cell-age" style={{ fontSize: '14px', color: '#64748b' }}>{calcAge(p.dob, p.age)}</div>
                <div className="pl-cell-intake">
                  {isDischarged
                    ? <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '99px', background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}>{formatDischargeDate(p.discharged_at)}</span>
                    : (p.intake_completed
                        ? <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '99px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}> Done</span>
                        : <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '99px', background: '#fefce8', color: '#ca8a04', border: '1px solid #fde68a' }}>Pending</span>)
                  }
                </div>
                <div className="pl-cell-actions" style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => onSelectPatient(p)} style={{ padding: '5px 10px', background: isDischarged ? '#f1f5f9' : TEAL, color: isDischarged ? '#64748b' : 'white', border: isDischarged ? '1px solid #e2e8f0' : 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: FONT }}>Open</button>
                  {!isDemo && !isDischarged && (
                    <button onClick={() => setConfirmDelete(p)} style={{ padding: '5px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: FONT }}>Delete</button>
                  )}
                </div>
              </div>
            )
          })}

          {displayPatients.length === 0 && (
            <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
              {search ? `No ${tab} patients matching "${search}"` : tab === 'discharged' ? 'No discharged patients.' : 'No active patients.'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PatientList